/**
 * Staged folders — SharePoint folders the platform maps into the run sandbox
 * before the agent loop starts.
 *
 * For each declared folder the runtime writes a recursive listing to
 * `/workspace/staged/<name>/manifest.json`, prefetches the files when the
 * folder is small, and mounts a read-only `fetch_staged_file` tool for the
 * rest. The declaration is wiring only: which file to read and what to do
 * with it stays in the operating procedure. It never changes tool authority
 * and the platform never writes anything back on the agent's behalf.
 *
 * This module also owns the MOUNT TABLE derivation — the pure mapping from a
 * declaration to sandbox paths — because both the runtime (prompt render,
 * materialize) and the web app (agent Details view) need it, and the web app
 * cannot import the runtime package.
 *
 * See docs/exec-plans/active/staged-folders-retire-sources-destinations.md.
 */
import { z } from "zod";

import { ITEM_ID_SOURCES, type ItemIdSource } from "./files";

/** Names become sandbox directory names — keep them path-safe. */
export const STAGED_FOLDER_NAME_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export const stagedFolderBindingSchema = z
  .object({
    /** Provider key. SharePoint is the only provider with a folder walker today. */
    provider: z.literal("sharepoint"),
    /** Site web URL, e.g. https://contoso.sharepoint.com/sites/Ops. */
    url: z.string().min(1),
    /** Folder path relative to the site's default document library ("" = root). */
    path: z.string(),
  })
  .strict();

export const stagedFolderSchema = z
  .object({
    name: z.string().regex(STAGED_FOLDER_NAME_PATTERN, {
      message: "staged folder names must be lowercase alphanumeric with - or _ (max 64 chars)",
    }),
    from: stagedFolderBindingSchema,
  })
  .strict();

export const stagedFoldersSchema = z.array(stagedFolderSchema);

export type StagedFolderBinding = z.infer<typeof stagedFolderBindingSchema>;
export type StagedFolder = z.infer<typeof stagedFolderSchema>;

/**
 * The minimal config surface this module reads (structural subset of
 * AgentConfigData). `sources` is the retired declaration whose folder-kind
 * entries still stage — see {@link stagedFoldersFromConfig}.
 */
export type StagedFoldersConfig = {
  staged_folders?: StagedFolder[] | undefined;
  sources?: unknown;
};

export interface StagedFolderProblem {
  field: "staged_folders";
  message: string;
}

/** Cross-entry coherence the schema alone cannot express. Empty means valid. */
export function validateStagedFolders(folders: readonly StagedFolder[]): StagedFolderProblem[] {
  const out: StagedFolderProblem[] = [];
  const seen = new Set<string>();
  for (const folder of folders) {
    if (seen.has(folder.name)) {
      out.push({
        field: "staged_folders",
        message: `duplicate staged folder name "${folder.name}"`,
      });
    }
    seen.add(folder.name);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Legacy normalization — DELETE in Milestone 4 of the ExecPlan
// ---------------------------------------------------------------------------

/**
 * The retired `sources` shape, read leniently: only folder-kind entries with
 * a SharePoint binding are honoured (as staged folders); everything else the
 * old declaration carried is ignored at run time and reported by the
 * dashboard. Kept until every current agent version in production has been
 * republished with `staged_folders`.
 */
const legacySourceSchema = z
  .object({
    name: z.string(),
    kind: z.enum(["file", "folder", "trigger"]).optional(),
    from: z
      .object({ provider: z.string(), url: z.string().optional(), path: z.string().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();

/**
 * The effective staged folders of a config: `staged_folders` when present,
 * else the folder-kind entries of the retired `sources` list. Never throws —
 * read paths must tolerate historical config.
 */
export function stagedFoldersFromConfig(config: StagedFoldersConfig): StagedFolder[] {
  if (config.staged_folders) return config.staged_folders;
  const legacy = z.array(legacySourceSchema).safeParse(config.sources);
  if (!legacy.success) return [];
  const out: StagedFolder[] = [];
  const seen = new Set<string>();
  for (const source of legacy.data) {
    if ((source.kind ?? "folder") !== "folder") continue;
    if (source.from?.provider !== "sharepoint" || !source.from.url) continue;
    if (!STAGED_FOLDER_NAME_PATTERN.test(source.name) || seen.has(source.name)) continue;
    seen.add(source.name);
    out.push({
      name: source.name,
      from: { provider: "sharepoint", url: source.from.url, path: source.from.path ?? "" },
    });
  }
  return out;
}

/**
 * What a legacy declaration carried that no longer runs, for the dashboard
 * warning: every non-folder source and every destination, by name.
 */
export function retiredDeclarationEntries(config: { sources?: unknown; destinations?: unknown }): {
  retired: string[];
  hasLegacyKeys: boolean;
} {
  const hasLegacyKeys = config.sources !== undefined || config.destinations !== undefined;
  const retired: string[] = [];
  const legacySources = z.array(legacySourceSchema).safeParse(config.sources);
  if (legacySources.success) {
    for (const source of legacySources.data) {
      if ((source.kind ?? "folder") !== "folder" || source.from?.provider !== "sharepoint") {
        retired.push(source.name);
      }
    }
  }
  const destinations = z
    .array(z.object({ name: z.string() }).passthrough())
    .safeParse(config.destinations);
  if (destinations.success) for (const d of destinations.data) retired.push(d.name);
  return { retired, hasLegacyKeys };
}

// ---------------------------------------------------------------------------
// Mount table
// ---------------------------------------------------------------------------

export const STAGED_FOLDERS_DIR = "/workspace/staged";
/**
 * Recursive listing at `<dir>/manifest.json`: `{ folder, truncated, truncation?,
 * prefetched, entries: [{ path, name, size, modified, id?, sourceKind?, scope?, eTag? }] }`.
 * `truncation` is present exactly when `truncated` is true:
 * `{ reasons: ("depth" | "entries")[], folders: string[] }`.
 */
export const STAGED_FOLDER_MANIFEST_NAME = "manifest.json";

/**
 * The id space each binding provider's listed items identify in. A new provider
 * cannot be added to the binding union without declaring one here.
 */
export const STAGED_FOLDER_ID_SOURCE = {
  sharepoint: ITEM_ID_SOURCES.MICROSOFT_GRAPH,
} as const satisfies Record<StagedFolderBinding["provider"], ItemIdSource>;

export type StagedFolderMount = {
  name: string;
  /** Sandbox directory (trailing slash). */
  dir: string;
  from: StagedFolderBinding;
};

/** Derive the mount table — deterministic in the declaration alone, no I/O. */
export function buildStagedFolderMounts(folders: readonly StagedFolder[]): StagedFolderMount[] {
  const seen = new Set<string>();
  const mounts: StagedFolderMount[] = [];
  for (const folder of folders) {
    if (seen.has(folder.name)) continue;
    seen.add(folder.name);
    mounts.push({
      name: folder.name,
      dir: `${STAGED_FOLDERS_DIR}/${folder.name}/`,
      from: folder.from,
    });
  }
  return mounts;
}
